import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { isNil } from 'lodash'

import { FilterForm } from '@/components/filters/FilterForm'
import { FormField } from '@/components/forms/FormField'
import { PageHeader } from '@/components/headers/PageHeader'
import { ErrorMessage } from '@/components/forms/ErrorMessage'
import { BasicModal } from '@/components/layout/modals/BasicModal'
import Select from '@/components/forms/Select'
import { getOrganizationOptions } from '@/utils/helpers'
import { fetchOrganizations } from '@/reducers/organizations/organizations.actions'
import { fetchOmnichannelUsage } from '@/reducers/omnichannelUsage/omnichannelUsage.actions'

import { UsageProgressCard } from './UsageProgressCard'
import { UsageByChannelWidget } from './UsageByChannelWidget'
import { formatContractWindow } from './helpers'

import './OmnichannelUsagePage.scss'

const HOW_IT_WORKS_COPY = (
  <div className="omnichannel-usage__how-modal-content">
    <p>
      Each <strong>email</strong> and each <strong>SMS</strong> counts as one interaction.
    </p>
    <p>
      An entire <strong>chat conversation</strong> counts as one interaction, no matter how many
      messages are exchanged inside it.
    </p>
    <p className="muted-text">
      Your contract limit covers one contract year. Both the progress bar and the table above show
      your usage across the exact dates shown on this page.
    </p>
  </div>
)

export const OmnichannelUsagePage = () => {
  const dispatch = useDispatch()

  const currentUser = useSelector((state) => state.currentUser)
  const organizations = useSelector((state) => state.organizations)
  const usage = useSelector((state) => state.omnichannelUsage)

  const isBaltoAdmin = currentUser.organizationid === 1

  const [selectedOrgId, setSelectedOrgId] = useState(currentUser.organizationid)
  const [howOpen, setHowOpen] = useState(false)
  const requestIdRef = useRef(0)

  useEffect(() => {
    if (isBaltoAdmin) {
      dispatch(fetchOrganizations())
    }
  }, [dispatch, isBaltoAdmin])

  // No date range: the server answers with the contract year, which is the only
  // window this page shows (QCER-1104).
  //
  // `requestId` drops all-but-latest responses. Switching organization twice
  // quickly leaves two requests in flight, and the slower one arriving last
  // would otherwise repaint the earlier org's counts over the newer org's
  // result — the same stale-numbers failure this page is meant to fix.
  useEffect(() => {
    const requestId = ++requestIdRef.current
    dispatch(
      fetchOmnichannelUsage({
        organizationId: selectedOrgId,
        isCurrent: () => requestId === requestIdRef.current,
      })
    )
  }, [dispatch, selectedOrgId])

  const organizationOptions = useMemo(
    () => (organizations ? getOrganizationOptions(organizations) : []),
    [organizations]
  )

  const contractWindow = useMemo(
    () => formatContractWindow(usage.contractStart, usage.contractEnd),
    [usage.contractStart, usage.contractEnd]
  )

  const hasError = !isNil(usage.error)

  return (
    <div className="omnichannel-usage" data-testid="omnichannel-usage-page">
      <PageHeader title="Omnichannel Usage" data-testid="omnichannel-usage-page-header" />

      {isBaltoAdmin && (
        <FilterForm small sticky={false}>
          <FormField>
            <label htmlFor="omnichannel-usage-org-filter">Organization</label>
            <Select
              inputId="omnichannel-usage-org-filter"
              options={organizationOptions}
              value={selectedOrgId}
              onChange={({ value }) => setSelectedOrgId(value)}
              data-testid="omnichannel-usage-org-filter"
              search
            />
          </FormField>
        </FilterForm>
      )}

      {/* On failure the counts are cleared, so rendering the cards would show a
          confident 0 for usage. Show why there are no numbers instead. A retry
          clears `error` as it starts, so this doesn't need to test `loading`. */}
      {hasError ? (
        <ErrorMessage
          content="We couldn't load usage for this organization. Refresh to try again."
          data-testid="omnichannel-usage-error"
        />
      ) : (
        <>
          <UsageProgressCard
            used={usage.headline?.total ?? 0}
            limit={usage.contractLimit}
            contractWindow={contractWindow}
            onHowItWorksClick={() => setHowOpen(true)}
            loading={usage.loading}
          />

          <UsageByChannelWidget
            byChannel={usage.table?.byChannel ?? []}
            total={usage.table?.total ?? 0}
            loading={usage.loading}
            contractWindow={contractWindow}
          />
        </>
      )}

      <BasicModal title="How usage works" show={howOpen} onClose={() => setHowOpen(false)}>
        {HOW_IT_WORKS_COPY}
      </BasicModal>
    </div>
  )
}

export default OmnichannelUsagePage
